import { RiskMatrix } from '@models/clients/clients.details/compliance/riskMatrix';
import { Compliance } from '@models/clients/clients.details/compliance/compliance';

export class AddComplianceModalController {
    public compliance: Compliance = {};
    public client: any;
    public riskMatrixPanel: any;
    public sourceOfFundsPanel: any;
    public dueDiligencePanel: any;
    
    // Loading states for each table
    public riskMatrixLoading: boolean = false;
    public sourceOfFundsLoading: boolean = false;
    public dueDiligenceLoading: boolean = false;
    
    // Reusable table properties for risk matrix
    public riskMatrixColumns = [
        { key: 'riskRating', label: 'Risk Rating', width: 'col-md-2' },
        { key: 'dateOfReview', label: 'Date of Review', width: 'col-md-3', type: 'date' },
        { key: 'reviewFrequency', label: 'Review Frequency', width: 'col-md-3' },
        { key: 'nextReviewDate', label: 'Next Review Date', width: 'col-md-3', type: 'date' },
        { key: 'actions', label: 'Actions', width: 'col-md-1', type: 'actions' }
    ];

    public riskMatrixActions = [
        {
            icon: 'edit',
            cssClass: '',
            title: 'Edit Risk Matrix',
            onClick: (item: RiskMatrix, index: number) => this.editRiskMatrix(item)
        }
    ];

    // Reusable table properties for source of funds
    public sourceOfFundsColumns = [
        { key: 'detailsOfSourceOfFunds', label: 'Details of Source of Funds', width: 'col-md-3' },
        { key: 'supportingDocumentReceived', label: 'Supporting Document received on source of funds', width: 'col-md-3' },
        { key: 'linkToSupportingDocuments', label: 'Link to Supporting Documents', width: 'col-md-2' },
        { key: 'politicallyExposed', label: 'Politically Exposed', width: 'col-md-2' },
        { key: 'comments', label: 'Comments', width: 'col-md-1' },
        { key: 'actions', label: 'Actions', width: 'col-md-1', type: 'actions' }
    ];

    public sourceOfFundsActions = [
        {
            icon: 'edit',
            cssClass: '',
            title: 'Edit Source of Funds',
            onClick: (item: any, index: number) => this.editSourceOfFunds(item)
        }
    ];

    // Reusable table properties for due diligence
    public dueDiligenceColumns = [
        { key: 'personEntity', label: 'Person / Entity', width: 'col-md-3' },
        { key: 'residency', label: 'Residency', width: 'col-md-2' },
        { key: 'relationshipDesignation', label: 'Relationship Designation', width: 'col-md-3' },
        { key: 'complianceDocuments', label: 'Compliance Documents', width: 'col-md-2' },
        { key: 'actions', label: 'Actions', width: 'col-md-1', type: 'actions' }
    ];

    public dueDiligenceActions = [
        {
            icon: 'edit',
            cssClass: '',
            title: 'Edit Due Diligence',
            onClick: (item: any, index: number) => this.editDueDiligence(item)
        }
    ];

    /** @ngInject */
    constructor(
        private $mdDialog: angular.material.IDialogService,
        private $element: angular.IAugmentedJQuery
    ) {}

    public $onInit(): void {
        // Initialize compliance object with default values
        this.compliance = {
            name: (this.client && this.client.name) || '',
            assetManagementMandate: null,
            linkToAssetManagementMandate: '',
            amendedDate: null,
            comments: '',
            nextReviewDate: null,
            riskMatrix: [],
            sourceOfFunds: [],
            dueDiligence: []
        };
    }

    public $postLink(): void {
        // Get reference to the risk matrix panel component
        const panelElement = this.$element.find('risk-matrix-panel');
        if (panelElement.length > 0) {
            const scope = panelElement.isolateScope();
            if (scope && (scope as any).$ctrl) {
                this.riskMatrixPanel = (scope as any).$ctrl;
            }
        }

        // Get reference to the source of funds panel component
        const sourceOfFundsPanelElement = this.$element.find('source-of-funds-panel');
        if (sourceOfFundsPanelElement.length > 0) {
            const scope = sourceOfFundsPanelElement.isolateScope();
            if (scope && (scope as any).$ctrl) {
                this.sourceOfFundsPanel = (scope as any).$ctrl;
            }
        }

        // Get reference to the due diligence panel component
        const dueDiligencePanelElement = this.$element.find('due-diligence-panel');
        if (dueDiligencePanelElement.length > 0) {
            const scope = dueDiligencePanelElement.isolateScope();
            if (scope && (scope as any).$ctrl) {
                this.dueDiligencePanel = (scope as any).$ctrl;
            }
        }
    }

    public save(): void {
        // Validate required fields
        if (!this.compliance.assetManagementMandate) {
            // Show error message
            return;
        }

        // Close dialog and return the compliance data
        this.$mdDialog.hide(this.compliance);
    }

    public cancel(): void {
        this.$mdDialog.cancel();
    }

    public addRiskMatrix(): void {
        if (this.riskMatrixPanel) {
            this.riskMatrixPanel.open();
        }
    }

    public editRiskMatrix(riskMatrix: RiskMatrix): void {
        if (this.riskMatrixPanel) {
            this.riskMatrixPanel.open(riskMatrix);
        }
    }



    public onRiskMatrixSave(riskMatrix: RiskMatrix): void {
        // Add null check for riskMatrix parameter
        if (!riskMatrix) {
            console.error('RiskMatrix parameter is undefined or null');
            return;
        }

        // Set loading state
        this.riskMatrixLoading = true;

        // Simulate async operation (replace with actual API call)
        setTimeout(() => {
            if (!this.compliance.riskMatrix) {
                this.compliance.riskMatrix = [];
            }
            
            // Check if this is an edit (has id) or new item
            const existingIndex = this.compliance.riskMatrix.findIndex(item => item.id === riskMatrix.id);
            if (existingIndex >= 0) {
                // Update existing item
                this.compliance.riskMatrix[existingIndex] = { ...riskMatrix };
            } else {
                // Add new item with generated id
                const newRiskMatrix = { ...riskMatrix };
                newRiskMatrix.id = Date.now(); // Simple id generation for demo
                this.compliance.riskMatrix.push(newRiskMatrix);
            }

            // Clear loading state
            this.riskMatrixLoading = false;
        }, 1000); // Simulate 1 second delay
    }

    public onRiskMatrixClose(): void {
        // Panel closed, no action needed
    }

    public onRiskMatrixDelete(riskMatrix: RiskMatrix): void {
        if (this.compliance.riskMatrix && riskMatrix && riskMatrix.id) {
            // Set loading state
            this.riskMatrixLoading = true;

            // Simulate async operation (replace with actual API call)
            setTimeout(() => {
                const index = this.compliance.riskMatrix.findIndex(item => item.id === riskMatrix.id);
                if (index >= 0) {
                    this.compliance.riskMatrix.splice(index, 1);
                }

                // Clear loading state
                this.riskMatrixLoading = false;
            }, 1000); // Simulate 1 second delay
        }
    }

    // Source of Funds methods
    public addSourceOfFunds(): void {
        if (this.sourceOfFundsPanel) {
            this.sourceOfFundsPanel.open();
        }
    }

    public editSourceOfFunds(item: any): void {
        if (this.sourceOfFundsPanel) {
            this.sourceOfFundsPanel.open(item);
        }
    }

    public deleteSourceOfFunds(index: number): void {
        if (this.compliance.sourceOfFunds && index >= 0 && index < this.compliance.sourceOfFunds.length) {
            this.compliance.sourceOfFunds.splice(index, 1);
        }
    }

    public onSourceOfFundsSave(sourceOfFunds: any): void {
        // Add null check for sourceOfFunds parameter
        if (!sourceOfFunds) {
            console.error('SourceOfFunds parameter is undefined or null');
            return;
        }

        // Set loading state
        this.sourceOfFundsLoading = true;

        // Simulate async operation (replace with actual API call)
        setTimeout(() => {
            if (!this.compliance.sourceOfFunds) {
                this.compliance.sourceOfFunds = [];
            }
            
            // Check if this is an edit (has id) or new item
            const existingIndex = this.compliance.sourceOfFunds.findIndex(item => item.id === sourceOfFunds.id);
            if (existingIndex >= 0) {
                // Update existing item
                this.compliance.sourceOfFunds[existingIndex] = { ...sourceOfFunds };
            } else {
                // Add new item with generated id
                const newSourceOfFunds = { ...sourceOfFunds };
                newSourceOfFunds.id = Date.now(); // Simple id generation for demo
                this.compliance.sourceOfFunds.push(newSourceOfFunds);
            }

            // Clear loading state
            this.sourceOfFundsLoading = false;
        }, 1000); // Simulate 1 second delay
    }

    public onSourceOfFundsClose(): void {
        // Panel closed, no action needed
    }

    public onSourceOfFundsDelete(sourceOfFunds: any): void {
        if (this.compliance.sourceOfFunds && sourceOfFunds && sourceOfFunds.id) {
            // Set loading state
            this.sourceOfFundsLoading = true;

            // Simulate async operation (replace with actual API call)
            setTimeout(() => {
                const index = this.compliance.sourceOfFunds.findIndex(item => item.id === sourceOfFunds.id);
                if (index >= 0) {
                    this.compliance.sourceOfFunds.splice(index, 1);
                }

                // Clear loading state
                this.sourceOfFundsLoading = false;
            }, 1000); // Simulate 1 second delay
        }
    }

    // Due Diligence methods
    public addDueDiligence(): void {
        if (this.dueDiligencePanel) {
            this.dueDiligencePanel.open();
        }
    }

    public editDueDiligence(item: any): void {
        if (this.dueDiligencePanel) {
            this.dueDiligencePanel.open(item);
        }
    }

    public deleteDueDiligence(index: number): void {
        if (this.compliance.dueDiligence && index >= 0 && index < this.compliance.dueDiligence.length) {
            this.compliance.dueDiligence.splice(index, 1);
        }
    }

    public onDueDiligenceSave(dueDiligence: any): void {
        // Add null check for dueDiligence parameter
        if (!dueDiligence) {
            console.error('DueDiligence parameter is undefined or null');
            return;
        }

        // Set loading state
        this.dueDiligenceLoading = true;

        // Simulate async operation (replace with actual API call)
        setTimeout(() => {
            if (!this.compliance.dueDiligence) {
                this.compliance.dueDiligence = [];
            }
            
            // Check if this is an edit (has id) or new item
            const existingIndex = this.compliance.dueDiligence.findIndex(item => item.id === dueDiligence.id);
            if (existingIndex >= 0) {
                // Update existing item
                this.compliance.dueDiligence[existingIndex] = { ...dueDiligence };
            } else {
                // Add new item with generated id
                const newDueDiligence = { ...dueDiligence };
                newDueDiligence.id = Date.now(); // Simple id generation for demo
                this.compliance.dueDiligence.push(newDueDiligence);
            }

            // Clear loading state
            this.dueDiligenceLoading = false;
        }, 1000); // Simulate 1 second delay
    }

    public onDueDiligenceClose(): void {
        // Panel closed, no action needed
    }

    public onDueDiligenceDelete(dueDiligence: any): void {
        if (this.compliance.dueDiligence && dueDiligence && dueDiligence.id) {
            // Set loading state
            this.dueDiligenceLoading = true;

            // Simulate async operation (replace with actual API call)
            setTimeout(() => {
                const index = this.compliance.dueDiligence.findIndex(item => item.id === dueDiligence.id);
                if (index >= 0) {
                    this.compliance.dueDiligence.splice(index, 1);
                }

                // Clear loading state
                this.dueDiligenceLoading = false;
            }, 1000); // Simulate 1 second delay
        }
    }
}

export const AddComplianceModalComponent: angular.IComponentOptions = {
    template: require('./add-compliance-modal.html'),
    controller: AddComplianceModalController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/compliance/modals/add-compliance-modal.ts